import { useCallback } from "react"
import type { SmsChat } from "@stockline/shared/types/communications/sms-chat"
import { SMS_CHAT_TABLE } from "../../../lib/offline/resources/communications/sms-chat/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

/**
 * Whitelist of fields the V2 push pipeline accepts on update.
 * Mirrors the server-side `SmsChatUpdateFields` interface — keep in sync.
 */
export interface UpdateSmsChatInput {
  displayName?: string | null
  aiContext?: string | null
  aiAnalysisEnabled?: boolean
}

export type OptimisticRollback = () => Promise<void>

export function useSmsChatMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const updateChat = useCallback(
    async (id: string, input: UpdateSmsChatInput): Promise<void> => {
      const existing = await storage.get<SmsChat>(SMS_CHAT_TABLE, id)
      if (!existing) return

      const updated: SmsChat = {
        ...existing,
        ...input,
        updatedAt: new Date().toISOString(),
      }

      await storage.put(SMS_CHAT_TABLE, updated)
      await changeQueue.enqueue("sms-chats", {
        op: "update",
        id,
        data: input,
      })
      void engine.pushChanges("sms-chats")
    },
    [storage, changeQueue, engine]
  )

  // Bulk variant of `markAsReadOptimistic` — flips every Dexie row's
  // `unreadCount` to 0 in one `putMany`. The chat list reads from Dexie via
  // `useSmsChats`, so without this the unread badges stay visible until the
  // per-chat `sms-chat.updated` SSE events arrive (one round-trip per
  // chat, choppy on large lists). The legacy backend's bulk endpoint stays
  // the canonical writer; the per-row SSE events still arrive and
  // reconcile syncId — this just lights up the UI instantly.
  const markAllAsReadOptimistic =
    useCallback(async (): Promise<OptimisticRollback | null> => {
      try {
        const all = await storage.getAll<SmsChat>(SMS_CHAT_TABLE)
        const targets = all.filter((c) => !c.deletedAt && c.unreadCount > 0)
        if (targets.length === 0) return null

        const previousById = new Map<
          string,
          { unreadCount: number; syncId: number }
        >()
        for (const c of targets) {
          previousById.set(c.id, {
            unreadCount: c.unreadCount,
            syncId: c.syncId ?? 0,
          })
        }

        await storage.putMany<SmsChat>(
          SMS_CHAT_TABLE,
          targets.map((c) => ({ ...c, unreadCount: 0 }))
        )

        return async () => {
          try {
            const fresh = await storage.getAll<SmsChat>(SMS_CHAT_TABLE)
            const restored: SmsChat[] = []
            for (const c of fresh) {
              const prev = previousById.get(c.id)
              if (!prev) continue
              // Strict `<`: a newer SSE write wins; same-syncId rows are
              // still ours to restore.
              if (prev.syncId < (c.syncId ?? 0)) continue
              restored.push({ ...c, unreadCount: prev.unreadCount })
            }
            if (restored.length > 0) {
              await storage.putMany<SmsChat>(SMS_CHAT_TABLE, restored)
            }
          } catch (error) {
            console.warn("markAllAsReadOptimistic rollback failed", error)
          }
        }
      } catch (error) {
        console.warn("markAllAsReadOptimistic failed", error)
        return null
      }
    }, [storage])

  // Optimistically clears `unreadCount` in Dexie and returns a rollback
  // closure. `unreadCount` is not part of the V2 push whitelist — the
  // canonical write still happens against the legacy backend, which bumps
  // sync_id and fans out `sms-chat.updated` so Dexie reconciles on the SSE
  // round-trip. Pure local optimism keeps the chat list at zero instantly
  // when the user opens a chat.
  const markAsReadOptimistic = useCallback(
    async (id: string): Promise<OptimisticRollback | null> => {
      const existing = await storage.get<SmsChat>(SMS_CHAT_TABLE, id)
      if (!existing || existing.unreadCount === 0) return null

      const previousUnreadCount = existing.unreadCount
      const previousSyncId = existing.syncId ?? 0
      await storage.put<SmsChat>(SMS_CHAT_TABLE, {
        ...existing,
        unreadCount: 0,
      })

      return async () => {
        const current = await storage.get<SmsChat>(SMS_CHAT_TABLE, id)
        if (!current) return
        // Skip rollback if SSE has already delivered a newer row — its
        // unreadCount is now authoritative. Strict `<` so a same-syncId
        // server push is left alone.
        if (previousSyncId < (current.syncId ?? 0)) return
        // Restore only the field we touched; other local edits (rename,
        // aiContext, etc.) that landed during the round-trip must survive.
        await storage.put<SmsChat>(SMS_CHAT_TABLE, {
          ...current,
          unreadCount: previousUnreadCount,
        })
      }
    },
    [storage]
  )

  // Optimistically flip `archived` in Dexie. Mirrors `markAsReadOptimistic`
  // — `archived` isn't on the V2 push whitelist (the legacy FastAPI route
  // owns the write + emits `sms-chat.updated` via RabbitMQ → SSE → Dexie),
  // but the chat list reads from Dexie, so without this write the UI
  // doesn't change until the SSE round-trip completes (~100–500ms).
  //
  // Dexie failures (corrupted store, quota exceeded, browser without
  // IndexedDB) MUST NOT abort the calling mutation — the REST call is
  // still our canonical write path. Swallow + log; SSE backfill will
  // converge Dexie when the network call succeeds.
  const setArchivedOptimistic = useCallback(
    async (
      id: string,
      archived: boolean
    ): Promise<OptimisticRollback | null> => {
      try {
        const existing = await storage.get<SmsChat>(SMS_CHAT_TABLE, id)
        if (!existing || existing.archived === archived) return null

        const previousArchived = existing.archived
        const previousSyncId = existing.syncId ?? 0
        await storage.put<SmsChat>(SMS_CHAT_TABLE, {
          ...existing,
          archived,
        })

        return async () => {
          try {
            const current = await storage.get<SmsChat>(SMS_CHAT_TABLE, id)
            if (!current) return
            if (previousSyncId < (current.syncId ?? 0)) return
            await storage.put<SmsChat>(SMS_CHAT_TABLE, {
              ...current,
              archived: previousArchived,
            })
          } catch (error) {
            console.warn("setArchivedOptimistic rollback failed", error)
          }
        }
      } catch (error) {
        console.warn("setArchivedOptimistic failed", error)
        return null
      }
    },
    [storage]
  )

  // Optimistically flip `enabled` in Dexie (chat disable / re-enable).
  // The legacy backend cascades message soft-deletes on disable — those
  // ride their own `sms-message.deleted` SSE events, independent of this
  // chat-row update. Dexie failures swallowed for the same reason as
  // `setArchivedOptimistic`.
  const setEnabledOptimistic = useCallback(
    async (
      id: string,
      enabled: boolean
    ): Promise<OptimisticRollback | null> => {
      try {
        const existing = await storage.get<SmsChat>(SMS_CHAT_TABLE, id)
        if (!existing || existing.enabled === enabled) return null

        const previousEnabled = existing.enabled
        const previousSyncId = existing.syncId ?? 0
        await storage.put<SmsChat>(SMS_CHAT_TABLE, {
          ...existing,
          enabled,
        })

        return async () => {
          try {
            const current = await storage.get<SmsChat>(SMS_CHAT_TABLE, id)
            if (!current) return
            if (previousSyncId < (current.syncId ?? 0)) return
            await storage.put<SmsChat>(SMS_CHAT_TABLE, {
              ...current,
              enabled: previousEnabled,
            })
          } catch (error) {
            console.warn("setEnabledOptimistic rollback failed", error)
          }
        }
      } catch (error) {
        console.warn("setEnabledOptimistic failed", error)
        return null
      }
    },
    [storage]
  )

  // Optimistically flip `groupId` in Dexie (chat sharing toggle).
  // Group sharing is what triggers fan-out — once `group_id` is set,
  // every member of the group sees their own "seat" row for the same
  // external phone number. `dedupeChatsByConversationKey` collapses
  // those seats in the chat list, but only AFTER Dexie reflects the
  // new group_id, so the optimistic write is required for the toggle
  // to feel instant.
  const setGroupOptimistic = useCallback(
    async (
      id: string,
      groupId: string | null
    ): Promise<OptimisticRollback | null> => {
      try {
        const existing = await storage.get<SmsChat>(SMS_CHAT_TABLE, id)
        if (!existing || existing.groupId === groupId) return null

        const previousGroupId = existing.groupId
        const previousSyncId = existing.syncId ?? 0
        await storage.put<SmsChat>(SMS_CHAT_TABLE, {
          ...existing,
          groupId,
        })

        return async () => {
          try {
            const current = await storage.get<SmsChat>(SMS_CHAT_TABLE, id)
            if (!current) return
            if (previousSyncId < (current.syncId ?? 0)) return
            await storage.put<SmsChat>(SMS_CHAT_TABLE, {
              ...current,
              groupId: previousGroupId,
            })
          } catch (error) {
            console.warn("setGroupOptimistic rollback failed", error)
          }
        }
      } catch (error) {
        console.warn("setGroupOptimistic failed", error)
        return null
      }
    },
    [storage]
  )

  // Optimistically flip `aiAnalysisEnabled` in Dexie AND enqueue the push.
  const setAiAnalysisEnabledOptimistic = useCallback(
    async (
      id: string,
      aiAnalysisEnabled: boolean
    ): Promise<OptimisticRollback | null> => {
      const existing = await storage.get<SmsChat>(SMS_CHAT_TABLE, id)
      if (!existing || existing.aiAnalysisEnabled === aiAnalysisEnabled)
        return null

      const previousValue = existing.aiAnalysisEnabled
      const previousSyncId = existing.syncId ?? 0
      await storage.put<SmsChat>(SMS_CHAT_TABLE, {
        ...existing,
        aiAnalysisEnabled,
      })
      await changeQueue.enqueue("sms-chats", {
        op: "update",
        id,
        data: { aiAnalysisEnabled },
      })
      void engine.pushChanges("sms-chats")

      return async () => {
        const current = await storage.get<SmsChat>(SMS_CHAT_TABLE, id)
        if (!current) return
        if (previousSyncId < (current.syncId ?? 0)) return
        await storage.put<SmsChat>(SMS_CHAT_TABLE, {
          ...current,
          aiAnalysisEnabled: previousValue,
        })
      }
    },
    [storage, changeQueue, engine]
  )

  return {
    updateChat,
    markAsReadOptimistic,
    markAllAsReadOptimistic,
    setArchivedOptimistic,
    setEnabledOptimistic,
    setGroupOptimistic,
    setAiAnalysisEnabledOptimistic,
  }
}
